import { useEffect, useRef } from 'react';
import { loadChessComSettings, saveChessComSettings } from '../lib/chesscom/settings';
import { syncChessCom } from '../lib/chesscom/sync';
import { analysisQueue } from '../lib/coaching/analysisQueue';
import { listGames, getReport } from '../lib/storage/db';

/**
 * Synchronise Chess.com une fois à l'ouverture de l'app (si un pseudo est
 * enregistré), puis met en file toute partie Chess.com encore sans rapport —
 * pas seulement celles tout juste importées, pour reprendre proprement après
 * un rechargement de page en plein milieu d'une analyse. Ne relance jamais
 * de synchro tant que l'app reste ouverte (pas de polling en arrière-plan).
 */
export function useChessComAutoSync(onLibraryChanged: () => void): void {
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    (async () => {
      const settings = loadChessComSettings();
      if (!settings.username.trim()) return;

      let importedAny = false;
      try {
        const result = await syncChessCom(settings);
        if (result.newestEndTime !== settings.lastSyncEndTime) {
          saveChessComSettings({ ...settings, lastSyncAt: new Date().toISOString(), lastSyncEndTime: result.newestEndTime });
        }
        importedAny = result.importedIds.length > 0;
      } catch {
        // Échec de synchro au démarrage (hors-ligne, Chess.com injoignable…) :
        // silencieux ici, l'onglet Chess.com reste utilisable manuellement
        // avec son propre message d'erreur si l'utilisateur y retourne.
      }

      if (importedAny) onLibraryChanged();

      if (settings.autoAnalyze) {
        const games = await listGames();
        const toAnalyze: string[] = [];
        for (const g of games) {
          if (g.source !== 'chesscom') continue;
          const report = await getReport(g.game.id);
          if (!report) toAnalyze.push(g.game.id);
        }
        if (toAnalyze.length > 0) analysisQueue.enqueue(toAnalyze);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
